import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject, catchError, map, of, switchMap, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { IntegrationsApiService, type ConnectMetaPayload } from '../data/integrations-api.service';
import { CONNECTOR_KEY } from '../catalog/provider-catalog';
import { FacebookSdkLoader, type FacebookSDK } from './facebook-sdk.loader';
import type { ConnectContext, ConnectorStrategy } from './connector-strategy';
import type { ConnectResult } from '../domain/connect-result';

/** Resultado do signup incorporado da Meta, antes do POST (connect ou add-number). */
type SignupOutcome =
  | { kind: 'payload'; payload: ConnectMetaPayload }
  | { kind: 'cancelled' }
  | { kind: 'error'; message: string };

const META_ORIGIN = 'https://www.facebook.com';
const META_EVENT_TYPE = 'WA_EMBEDDED_SIGNUP';

interface MetaSessionEventData {
  waba_id?: string;
  phone_number_id?: string;
  business_id?: string;
}

interface MetaSessionEvent {
  readonly type?: string;
  readonly event?: string;
  readonly data?: MetaSessionEventData;
}

@Injectable({ providedIn: 'root' })
export class MetaWhatsAppConnector implements ConnectorStrategy {
  readonly key = CONNECTOR_KEY.metaWhatsApp;
  readonly introHint =
    'Vamos abrir o cadastro incorporado do WhatsApp Business. Você verá uma janela do Facebook para autorizar sua conta. Todos os números da conta selecionada são conectados de uma vez — e depois você pode adicionar mais números, da mesma conta ou de outra conta Meta.';
  readonly cancelledHint = 'Você fechou a janela do Facebook sem concluir.';

  private readonly document = inject(DOCUMENT);
  private readonly sdkLoader = inject(FacebookSdkLoader);
  private readonly api = inject(IntegrationsApiService);

  /** SDK pronto → o clique pode disparar `FB.login` síncrono e abrir popup (não aba). */
  readonly ready = this.sdkLoader.ready;

  /** Aquece o SDK ao abrir o dialog, antes do clique de conectar. */
  preload(): void {
    this.sdkLoader.preload();
  }

  start(_ctx: ConnectContext): Observable<ConnectResult> {
    // Conexão inicial: signup → cria a integração (connectMeta).
    return this.runEmbeddedSignup().pipe(
      switchMap((outcome) => this.finalize(outcome, (payload) => this.api.connectMeta(payload),
        'Falha ao concluir a conexão com o WhatsApp.')),
    );
  }

  /** "Adicionar número": mesmo signup, mas re-sincroniza a integração existente
   * (aditivo, sem duplicar) em vez de criar outra. */
  addNumber(integrationId: string): Observable<ConnectResult> {
    return this.runEmbeddedSignup().pipe(
      switchMap((outcome) => this.finalize(outcome, (payload) => this.api.syncChannels(integrationId, payload),
        'Falha ao adicionar o número.')),
    );
  }

  addChannel(integrationId: string): Observable<ConnectResult> {
    return this.addNumber(integrationId);
  }

  /** Encapsula o embedded signup da Meta (janela do Facebook + captura de
   * waba/phone/business ids + authCode). Emite um SignupOutcome — o POST (connect
   * ou add-number) fica a cargo do chamador via finalize(). */
  private runEmbeddedSignup(): Observable<SignupOutcome> {
    return new Observable<SignupOutcome>((subscriber) => {
      const stop$ = new Subject<void>();
      const captured: MetaSessionEventData = {};

      const win = this.document.defaultView;
      if (!win) {
        subscriber.next({ kind: 'error', message: 'Não foi possível acessar o contexto do navegador.' });
        subscriber.complete();
        return;
      }

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== META_ORIGIN) return;
        const raw = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
        let parsed: MetaSessionEvent | undefined;
        try {
          parsed = JSON.parse(raw) as MetaSessionEvent;
        } catch {
          return;
        }
        if (parsed?.type !== META_EVENT_TYPE) return;
        if (parsed.event !== 'FINISH') return;
        const data = parsed.data ?? {};
        if (data.waba_id) captured.waba_id = data.waba_id;
        if (data.phone_number_id) captured.phone_number_id = data.phone_number_id;
        if (data.business_id) captured.business_id = data.business_id;
      };

      win.addEventListener('message', onMessage);

      const doLogin = (fb: FacebookSDK): void => {
        fb.login(
          (response) => {
            const code = response?.authResponse?.code;
            if (!code) {
              subscriber.next({ kind: 'cancelled' });
              subscriber.complete();
              return;
            }
            subscriber.next({
              kind: 'payload',
              payload: {
                authCode: code,
                wabaId: captured.waba_id ?? null,
                phoneNumberId: captured.phone_number_id ?? null,
                metaBusinessId: captured.business_id ?? null,
              },
            });
            subscriber.complete();
          },
          {
            config_id: environment.metaConfigId,
            response_type: 'code',
            override_default_response_type: true,
            extras: { version: 'v4' },
          },
        );
      };

      // SDK já quente (preload no intro): chama `FB.login` SÍNCRONO, ainda dentro do gesto do
      // clique — condição que o Chrome (macOS) exige pra abrir POPUP em vez de nova aba. Só cai
      // no caminho assíncrono (load().subscribe) se o SDK ainda não carregou.
      const warm = this.sdkLoader.peek();
      if (warm) {
        doLogin(warm);
      } else {
        this.sdkLoader
          .load()
          .pipe(takeUntil(stop$))
          .subscribe({
            next: (fb) => doLogin(fb),
            error: () => {
              subscriber.next({ kind: 'error', message: 'Não foi possível carregar o SDK do Facebook.' });
              subscriber.complete();
            },
          });
      }

      return () => {
        win.removeEventListener('message', onMessage);
        stop$.next();
        stop$.complete();
      };
    });
  }

  /** Converte o outcome do signup + o POST escolhido num ConnectResult. */
  private finalize(
    outcome: SignupOutcome,
    post: (payload: ConnectMetaPayload) => Observable<{ integration: { id: string } }>,
    errorMessage: string,
  ): Observable<ConnectResult> {
    if (outcome.kind === 'cancelled') return of<ConnectResult>({ kind: 'cancelled' });
    if (outcome.kind === 'error') return of<ConnectResult>({ kind: 'error', message: outcome.message, recoverable: true });
    return post(outcome.payload).pipe(
      map((resp): ConnectResult => ({ kind: 'success', integrationId: resp.integration.id })),
      catchError((err: { error?: { message?: string } }) =>
        of<ConnectResult>({ kind: 'error', message: err?.error?.message ?? errorMessage, recoverable: true })),
    );
  }
}
